import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import * as Icons from 'react-icons/fa';

const COLUMNS = [
  { id: 'lead', name: 'New Leads', color: '#3b82f6' },
  { id: 'booked', name: 'Booked', color: '#8b5cf6' },
  { id: 'completed', name: 'Completed', color: '#10b981' },
  { id: 'cancelled', name: 'Cancelled', color: '#ef4444' }
];

function Pipeline() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [bookings, setBookings] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  // New Booking State
  const [clientPhone, setClientPhone] = useState("");
  const [service, setService] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((u) => {
      setUser(u);
      if (u) {
        // Fetch bookings ONLY for the logged-in spa
        const q = query(collection(db, "businesses", u.uid, "bookings"));
        onSnapshot(q, (snapshot) => {
          setBookings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const moveTask = async (bookingId, newStatus) => {
    if (!user) return;
    await updateDoc(doc(db, "businesses", user.uid, "bookings", bookingId), {
      status: newStatus
    });
  };

  const handleAddBooking = async (e) => {
    e.preventDefault();
    if (!user || !clientPhone) return;
    await addDoc(collection(db, "businesses", user.uid, "bookings"), {
      clientPhone,
      service,
      status: 'lead',
      createdAt: serverTimestamp()
    });
    setShowAddModal(false);
    setClientPhone(""); setService("");
  };

  if (!user) return <div style={{ padding: '20px' }}>Loading Pipeline...</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#0f172a', fontSize: '26px', fontWeight: '800' }}>Booking Pipeline</h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Drag and drop to manage spa appointments.</p>
        </div>
        <button onClick={() => setShowAddModal(true)} style={{ background: '#10b981', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.FaPlus /> New Booking
        </button>
      </div>

      <div style={{ display: 'flex', gap: '20px', overflowX: 'auto', paddingBottom: '20px', alignItems: 'flex-start' }}>
        {COLUMNS.map(col => (
          <div key={col.id} style={{ minWidth: '280px', background: '#f1f5f9', borderRadius: '12px', padding: '15px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `3px solid ${col.color}`, paddingBottom: '10px' }}>
              <span style={{ fontWeight: '800', color: '#1e293b', textTransform: 'uppercase', fontSize: '12px' }}>{col.name}</span>
              <span style={{ background: '#cbd5e1', color: '#475569', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' }}>
                {bookings.filter(b => b.status === col.id).length}
              </span>
            </div>

            {bookings.filter(b => b.status === col.id).map(booking => (
              <div key={booking.id} style={{ background: 'white', padding: '15px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: '700', color: '#0f172a', marginBottom: '5px' }}>{booking.clientPhone}</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>{booking.service || 'No service specified'}</div>
                
                <div style={{ display: 'flex', gap: '5px' }}>
                  {COLUMNS.filter(c => c.id !== col.id).map(c => (
                    <button key={c.id} onClick={() => moveTask(booking.id, c.id)} style={{ padding: '4px 8px', fontSize: '10px', borderRadius: '4px', border: 'none', background: '#f8fafc', color: c.color, cursor: 'pointer', border: `1px solid ${c.color}` }}>
                      Move to {c.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ADD BOOKING MODAL */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '16px', width: '100%', maxWidth: '400px' }}>
            <h3>Create New Booking</h3>
            <form onSubmit={handleAddBooking} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <input required placeholder="Client Phone Number" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              <input placeholder="Service (e.g. Thai Massage)" value={service} onChange={(e) => setService(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '8px' }}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: '10px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px' }}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pipeline;